import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LogOut, HelpCircle, Settings, User, Shield, MessageCircle, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProjectSelector from './ProjectSelector';
import { useOnboarding } from '../contexts/OnboardingContext';
import NotificationCenter from './NotificationCenter';


const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { showOnboardingForReturningUser } = useOnboarding();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        showUserMenu &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        userButtonRef.current &&
        !userButtonRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header className="bg-super-dark-purple text-white px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side - Logo and Project Selector */}
        <div className="flex items-center space-x-6 flex-1">
          <div className="flex items-center space-x-2">
            <img
              src="/images/jarvis_logo.png"
              alt="Jarvis Logo"
              className="h-14 w-40 object-contain"
              style={{ display: 'block' }}
            />
            <span className="text-xl font-bold text-super-dark-purple">JARVIS</span>
          </div>

          <div className="flex items-center space-x-2" data-tour="project-selector">
            <span className="text-sm text-gray-300">CURRENT PROJECT</span>
            <ProjectSelector />
          </div>

          {/* FAQ Buttons - Centered */}
          <div className="flex-1 flex justify-center items-center space-x-3">
            <button
              onClick={() => navigate('/app/faq')}
              className="flex items-center space-x-2 bg-gradient-to-r from-dusky-purple to-super-dark-purple hover:from-purple-700 hover:to-indigo-900 px-5 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg"
              title="Ask FAQ AI Assistant"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm font-semibold">FAQs</span>
            </button>

            <button
              onClick={() => navigate('/app/faq-management')}
              className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 px-5 py-2.5 rounded-lg transition-all shadow-md hover:shadow-lg"
              title="Manage FAQs"
            >
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-semibold">FAQ Management</span>
            </button>
          </div>
        </div>

        {/* Right side - Help, Notifications and User menu */}
        <div className="flex items-center space-x-3">

          {/* Help/Tutorial Button */}
          <button
            data-tour="help-button"
            onClick={() => {
              showOnboardingForReturningUser();
              setShowUserMenu(false);
            }}
            className="flex items-center space-x-2 hover:bg-dusky-purple px-3 py-2 rounded-lg transition-colors"
            title="View Tutorial"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm hidden md:inline">Help</span>
          </button>

          {/* Notification Center */}
          <NotificationCenter />

          {/* User Menu */}
          <div className="relative">
            <button
              ref={userButtonRef}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 hover:bg-dusky-purple px-3 py-2 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-electric-blue rounded-full flex items-center justify-center text-super-dark-purple font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showUserMenu && (
              <div ref={userMenuRef} className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-white">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          user?.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-700' :
                          user?.role === 'MASTER' ? 'bg-purple-100 text-purple-700' :
                          user?.role === 'ADMIN' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          <Shield className="w-3 h-3 mr-1" />
                          {user?.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate('/app/settings');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                  >
                    <Settings className="w-4 h-4 text-gray-500" />
                    <span>Settings</span>
                  </button>
                </div>
                
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </header>
  );
};

export default Header;